import { cloneElement, type ReactElement, useId } from "react";

export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  /** Guidance shown between the label and the control, e.g. what to include. The control is described by it. */
  hint?: string;
  error?: string;
  children: ReactElement<{ invalid?: boolean; "aria-describedby"?: string }>;
  className?: string;
}

export function FormField({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
  className = "",
}: Readonly<FormFieldProps>) {
  const hintId = useId();
  const errorId = useId();
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-content-secondary">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-error-on-subtle">
            *
          </span>
        )}
      </label>
      {hint && (
        <p id={hintId} className="-mt-0.5 mb-1.5 text-xs text-content-tertiary">
          {hint}
        </p>
      )}
      {cloneElement(children, { invalid: Boolean(error), "aria-describedby": describedBy || undefined })}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-error-on-subtle">
          {error}
        </p>
      )}
    </div>
  );
}
